import { config as AtlasConfig } from '@sofarocean/atlas';
import { TimelineContext } from '@sofarocean/atlas/contexts/TimelineContext';
import { useWeatherInfo } from '@sofarocean/atlas/contexts/WeatherContext';
import { MapClickLayer, SpotterLayer } from '@sofarocean/atlas/maps';
import { GM_Point, SpotterMapData } from '@sofarocean/atlas/types';
import { PointLayer } from '@sofarocean/mapbox-context';
import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { addHours, isAfter, isBefore, roundToNearestHours } from 'date-fns';
import { find, groupBy, last, map, mapValues } from 'lodash';
import mapboxgl from 'mapbox-gl';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDeviceIdList } from '@/api/deviceIdList';
import { UseLeaderboardResult, useLeaderboard } from '@/api/leaderboard/useLeaderboard';
import { FleetDeviceLatestData, useFleetSpottersLatestData } from '@/api/oceansApi/useFleetSpottersLatestData';
import { useDisplayPreferences } from '@/api/userAccount';
import spotterImageBlueSelected from '@/assets/map/spotter-blue-selected.png';
import spotterImageBlue from '@/assets/map/spotter-blue.png';
import spotterImageYellowSelected from '@/assets/map/spotter-yellow-selected.png';
import spotterImageYellow from '@/assets/map/spotter-yellow.png';
import { MapControlsStack, WeatherLayerSelector } from '@/components/map/LayerControls';
import { SafeMap } from '@/components/map/SafeMap';
import { InspectorPopup } from '@/components/map/inspectors/InspectorPopup';
import { Graticules } from '@/components/map/layers/Graticules';
import { useMapUiContext } from '@/context/MapUiContext';
import { useTopSpotTourContext } from '@/context/TopSpotTourContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useNow } from '@/hooks/useNow';
import { useSpotterHash } from '@/hooks/useSpotterHash';
import { isFiniteNumber } from '@/utils';

export const DEFAULT_GEOLOCATION = { latitude: 37.7749, longitude: -122.4194 } as const;

export const TopSpotMap = () => {
    const { weatherLayers, mapInspector, setMapInspector } = useMapUiContext();
    const { focusedEntry, onUserInteraction } = useTopSpotTourContext();
    const { spotterId } = useSpotterHash();
    const navigate = useNavigate();
    const weatherMapFlag = useFeatureFlag('weatherMap');
    const [, setLastPannedHash] = useState<string>();

    const leaderboardEntries = useLeaderboard();
    const ribbonBySpotterId = useMemo(() => {
        const entriesBySpotterId = groupBy(
            leaderboardEntries.filter((entry) => 'spotterId' in entry),
            'spotterId'
        );
        const focusedRibbon = focusedEntry && 'ribbonPng' in focusedEntry ? focusedEntry.ribbonPng : undefined;
        return mapValues(entriesBySpotterId, (entries) => {
            const ribbons = map(entries, 'ribbonPng');
            if (focusedRibbon && ribbons.includes(focusedRibbon)) {
                // show the focused ribbon if present on this spotter; otherwise we'll just pick the first
                return focusedRibbon;
            }
            return ribbons[0];
        });
    }, [focusedEntry, leaderboardEntries]);

    const { data: deviceIdItems } = useDeviceIdList({ adminAllDevices: true });
    const spotterIds = useMemo(() => deviceIdItems?.data?.devices?.map((d) => d.spotterId) ?? [], [deviceIdItems]);
    const { data: fleetData } = useFleetSpottersLatestData({ spotters: spotterIds, useCache: true });
    const allDevices = fleetData?.data satisfies FleetDeviceLatestData[] | undefined;
    const devices = useMemo(() => {
        if (!allDevices) return undefined;

        const interactedSpotters: SpotterMapData[] = [];
        const spotters: SpotterMapData[] = [];

        allDevices.forEach((d) => {
            if (!isFiniteNumber(d.track[0].latitude)) return;

            const isSelected = d.spotterId === spotterId;
            const spotter = {
                label: d.name || d.spotterId,
                position: {
                    lat: d.track[0].latitude,
                    lon: d.track[0].longitude,
                },
                id: d.spotterId,
                timestamp: d.timeLastUpdatedUTC ?? '',
                icon: `${d.owner === 'grid' ? 'gridSpotter' : 'spotter'}${isSelected ? '-selected' : ''}`,
                wind: d.wind && {
                    ...d.wind,
                    ...d.wind.location,
                },
                waves: d.waves,
            };

            if (isSelected) {
                interactedSpotters.push(spotter);
            } else {
                spotters.push(spotter);
            }
        });
        return spotters.concat(interactedSpotters);
    }, [allDevices, spotterId]);

    useEffect(() => {
        // Close the map inspector if we navigated to a different spotter
        if (
            spotterId &&
            mapInspector?.type === 'spotter' &&
            mapInspector.spotterFamily === 'mine' &&
            mapInspector.spotterId !== spotterId
        ) {
            setMapInspector(undefined);
        }
    }, [spotterId, mapInspector, setMapInspector]);

    function onClickMap(position: GM_Point) {
        if (mapInspector) {
            setMapInspector(undefined);
        } else if (weatherMapFlag) {
            setMapInspector({ type: 'weather', position });
        }
    }

    return (
        <SafeMap
            mapType="main"
            showWeather={weatherMapFlag}
            allowMapZoomOnScroll
            zoom={1.8}
            weatherLayers={weatherLayers}
        >
            {weatherMapFlag && <WeatherUpdater />}
            <InspectorPopup />
            <MapClickLayer onClickMap={onClickMap} />
            <ScaleControl maxWidth={160} />
            {/* attachment points for spotter layers so we can turn them on an off without breaking beforeLayer */}
            <DummyLayer id="spottersAnchor" />
            <TourFlyEffect allDevices={allDevices} />
            {devices && (
                <SpotterLayer
                    beforeLayer="spottersAnchor"
                    eventHandlerPriority={2}
                    spotterMapData={devices}
                    onSpotterClick={(spotterId, spotter) => {
                        onUserInteraction();
                        setMapInspector({
                            type: 'spotter',
                            spotterFamily: 'mine',
                            spotterId: spotterId as string,
                            position: spotter.position,
                        });

                        // preempt the panner so we don't pan on map click-driven spotter selections
                        setLastPannedHash(`#spotter/${spotterId}`);
                        navigate(`#spotter/${spotterId}`);
                    }}
                    spotterIcons={[
                        { name: 'spotter', url: spotterImageYellow },
                        { name: 'gridSpotter', url: spotterImageBlue },
                        { name: 'spotter-selected', url: spotterImageYellowSelected },
                        { name: 'gridSpotter-selected', url: spotterImageBlueSelected },
                    ]}
                />
            )}
            {allDevices && (
                <RibbonLayer
                    allDevices={allDevices}
                    ribbonBySpotterId={ribbonBySpotterId}
                    onRibbonClick={(id, position) => {
                        onUserInteraction();
                        setMapInspector({ type: 'spotter', spotterFamily: 'mine', spotterId: id, position });
                        setLastPannedHash(`#spotter/${id}`);
                        navigate(`#spotter/${id}`);
                    }}
                />
            )}
            <MapControlsStack
                hideBelowSizePx={96}
                sx={{
                    right: 4,
                    top: 4,
                }}
            >
                {<WeatherLayerSelector />}
            </MapControlsStack>
            <Graticules />
        </SafeMap>
    );
};

const WEATHER_NOW_REFRESH_SEC = 10;

/**
 * This is a utility component with no DOM output, just so we can conditionally include it in the map and avoid calling
 * useWeatherInfo when weather is off.
 */
const WeatherUpdater = () => {
    const { setCurrentSimulatorTime, currentSimulatorTime } = useContext(TimelineContext);
    const { weatherVariableMetadata } = useWeatherInfo({ autoupdate: true });
    const now = useNow(WEATHER_NOW_REFRESH_SEC);

    const nextOutputTime = useMemo(() => {
        const metadata = find(weatherVariableMetadata, {
            modelID: AtlasConfig.weatherVariables.combinedWaves.modelID,
            variableID: 'significantWaveHeight',
        });

        if (!metadata?.enumeratedOutputTimes?.length) {
            return null;
        }

        for (const outputTimeStr of metadata.enumeratedOutputTimes) {
            const outputTime = new Date(outputTimeStr);
            if (isAfter(outputTime, now)) {
                return outputTime;
            }
        }
        return new Date(last(metadata.enumeratedOutputTimes)!);
    }, [weatherVariableMetadata, now]);

    useEffect(() => {
        // if we don't know about output times, it's probably because we haven't initialized weather yet.
        // set it a little into the future in the hope that we hit the most upcoming forecast so we don't
        // have to refresh once the output times arrive
        const targetForecastTime = nextOutputTime ?? roundToNearestHours(addHours(now, 3), { roundingMethod: 'floor' });
        if (!currentSimulatorTime || isBefore(currentSimulatorTime, targetForecastTime)) {
            try {
                setCurrentSimulatorTime(targetForecastTime);
            } catch (e) {
                console.warn('Exception setting weather simulator time', e);
            }
        }
    }, [currentSimulatorTime, nextOutputTime, now, setCurrentSimulatorTime]);

    return <></>;
};

export const ScaleControl = ({ maxWidth }: { maxWidth?: number }) => {
    const { data: displayPreferences } = useDisplayPreferences();
    const { map } = useContext(MapboxContext);
    const [scale, setScale] = useState<mapboxgl.ScaleControl>();

    useEffect(() => {
        if (map && !scale) {
            const newScale = new mapboxgl.ScaleControl({
                maxWidth,
                unit: 'imperial',
            });
            setScale(newScale);
            map.addControl(newScale);
        }
        return () => {
            if (scale) {
                try {
                    map?.removeControl(scale);
                } catch {
                    // ignore, scale's removeControl handler throws if the container has already been unmounted
                }
                setScale(undefined);
            }
        };
    }, [map, scale, maxWidth]);

    useEffect(() => {
        if (!scale) return;

        scale.setUnit(displayPreferences?.measuringDistance === 'km' ? 'metric' : 'imperial');
    }, [displayPreferences?.measuringDistance, scale]);

    return <></>;
};

/**
 * Just an attachment point so we can order layers that may be turned on or off, since having beforeLayer point to a nonexistent layer
 * breaks the after layer permanently.
 */
const DummyLayer = (props: { id: string; beforeLayer?: string }) => (
    <PointLayer {...props} style={{ paint: {}, layout: {} }} points={[]} type="circle" />
);

const TourFlyEffect = ({ allDevices }: { allDevices: FleetDeviceLatestData[] | undefined }) => {
    const { map } = useContext(MapboxContext);
    const { focusedEntry, onUserInteraction } = useTopSpotTourContext();
    const { setMapInspector, setWeatherLayer } = useMapUiContext();
    const navigate = useNavigate();

    useEffect(() => {
        if (!map) return;
        const onMoveStart = (e: { originalEvent?: Event }) => {
            if (e.originalEvent) onUserInteraction();
        };
        map.on('movestart', onMoveStart);
        return () => {
            map.off('movestart', onMoveStart);
        };
    }, [map, onUserInteraction]);

    useEffect(() => {
        if (!map || !allDevices || !focusedEntry) return;
        if (focusedEntry.stat === 'overall') {
            setMapInspector(undefined);
            map.flyTo({ zoom: 1.8, center: [map.getCenter().lng, -10] });
            return;
        }
        const entry = focusedEntry as UseLeaderboardResult;
        const focusedSpotterId = entry.spotterId;
        if (!focusedSpotterId) return;
        setWeatherLayer('raster', entry.weatherLayer ?? 'combinedWaves');
        const device = allDevices.find((d) => d.spotterId === focusedSpotterId);
        const location = device?.track?.[0];
        if (!location || !isFiniteNumber(location.latitude) || !isFiniteNumber(location.longitude)) return;

        navigate(`#spotter/${focusedSpotterId}`);
        setMapInspector(undefined);
        map.flyTo({ center: [location.longitude, location.latitude], zoom: 6 });
    }, [focusedEntry]); // eslint-disable-line react-hooks/exhaustive-deps

    return <></>;
};

const RIBBON_LAYER_ID = 'ribbonLayer';

const RibbonLayer = ({
    allDevices,
    ribbonBySpotterId,
    onRibbonClick,
}: {
    allDevices: FleetDeviceLatestData[];
    ribbonBySpotterId: Record<string, string>;
    onRibbonClick: (spotterId: string, position: { lat: number; lon: number }) => void;
}) => {
    const { map } = useContext(MapboxContext);

    const points = useMemo(
        () =>
            allDevices
                .filter((d) => ribbonBySpotterId[d.spotterId] && isFiniteNumber(d.track[0]?.latitude))
                .map((d) => ({
                    id: `ribbon-${d.spotterId}`,
                    latitude: d.track[0].latitude,
                    longitude: d.track[0].longitude,
                    properties: {
                        icon: ribbonBySpotterId[d.spotterId]!,
                        spotterId: d.spotterId,
                        lat: d.track[0].latitude,
                        lon: d.track[0].longitude,
                    },
                })),
        [allDevices, ribbonBySpotterId]
    );

    const symbolImages = useMemo(
        () => Object.values(ribbonBySpotterId).map((url) => ({ name: url, url })),
        [ribbonBySpotterId]
    );

    useEffect(() => {
        if (!map) return;
        const handler = (e: mapboxgl.MapMouseEvent & { features?: GeoJSON.Feature[] }) => {
            const props = e.features?.[0]?.properties as { spotterId: string; lat: number; lon: number } | undefined;
            // eslint-disable-next-line react/prop-types
            if (!props?.spotterId) return;
            // eslint-disable-next-line react/prop-types
            onRibbonClick(props.spotterId, { lat: props.lat, lon: props.lon });
        };
        map.on('click', RIBBON_LAYER_ID, handler);
        return () => {
            map.off('click', RIBBON_LAYER_ID, handler);
        };
    }, [map, onRibbonClick]);

    return (
        <PointLayer
            id={RIBBON_LAYER_ID}
            type="symbol"
            points={points}
            symbolImages={symbolImages}
            style={{
                layout: {
                    'icon-image': ['get', 'icon'],
                    'icon-size': ['interpolate', ['exponential', 2], ['zoom'], 2, 0.1, 5, 0.25],
                    'icon-offset': [80, 80],
                    'icon-allow-overlap': true,
                },
                paint: {},
            }}
        />
    );
};
